/**
 * The application frame: sidebar rail, retry banner, routed main column, alerts panel.
 * Prototype: docs/HRMS.jsx line 3717-3845.
 *
 * The shell owns exactly four pieces of state — whether the alerts panel is open, what
 * the alert count is, whether the service is reachable, and whether the rail is showing
 * on a narrow screen. Everything else is a route's business. `<Outlet />` sits inside a
 * Suspense boundary because heavy routes are code-split.
 *
 * The narrow-screen rail is the addition. The prototype was a desktop mock and the port
 * inherited its fixed 220 px rail with no breakpoint anywhere, so at 390 px the
 * navigation took 56% of the viewport and the content column — 170 px of it — clipped
 * everything it could not fit. Below `--bp-md` the rail becomes a drawer behind the menu
 * button in the top bar; the layout itself lives in `styles/shell.css`, since inline
 * styles cannot carry a media query.
 */
import { BellRing, Menu } from "lucide-react";
import { Suspense, useEffect, useState } from "react";
import { Outlet, useLocation } from "react-router-dom";
import { Spinner } from "@/components/ui";
import { useAlerts } from "@/hooks/useAlerts";
import { AlertsPanel } from "./AlertsPanel";
import { ErrorBanner } from "./ErrorBanner";
import { Sidebar } from "./Sidebar";

/** Shown while a lazily-imported route chunk loads. */
export function RouteFallback() {
  return (
    <div style={{ padding: 60, display: "grid", placeItems: "center" }}>
      <Spinner size={22} label="Loading" />
    </div>
  );
}

export function AppShell() {
  const [alertsOpen, setAlertsOpen] = useState(false);
  const [navOpen, setNavOpen] = useState(false);
  const alerts = useAlerts();
  const location = useLocation();

  // A route change from anywhere — a nav link, a stat card, the browser's Back button —
  // means the drawer has done its job.
  useEffect(() => setNavOpen(false), [location.pathname]);

  const alertCount = alerts.data?.count ?? 0;
  // Opening alerts from the off-canvas rail closes the rail: the panel sits under it
  // (z-index 55 against 90), so leaving it open hid the panel it had just opened.
  const toggleAlerts = () => {
    setAlertsOpen((open) => !open);
    setNavOpen(false);
  };

  return (
    <div className="bp-shell">
      <Sidebar
        alertCount={alertCount}
        alertsOpen={alertsOpen}
        onToggleAlerts={toggleAlerts}
        open={navOpen}
        onNavigate={() => setNavOpen(false)}
      />

      {/* Present only below --bp-md; `display: none` above it. */}
      {navOpen ? (
        <div className="bp-shell__scrim" onClick={() => setNavOpen(false)} aria-hidden="true" />
      ) : null}

      <div className="bp-shell__column">
        <div className="bp-shell__topbar">
          <button
            type="button"
            className="bp-shell__menu"
            onClick={() => setNavOpen((open) => !open)}
            aria-expanded={navOpen}
            aria-controls="app-sidebar"
          >
            <Menu size={15} aria-hidden="true" />
            Menu
          </button>
          <span className="bp-shell__brand">
            Botpresso<span>HR</span>
          </span>
          {/* The rail's Alerts item is two taps deep on a phone; this is the same
              toggle, one tap away. Named differently from the rail item so the two
              never read as one control. */}
          <button
            type="button"
            className="bp-shell__alerts"
            onClick={toggleAlerts}
            aria-expanded={alertsOpen}
            aria-label={alertCount > 0 ? `View alerts (${alertCount})` : "View alerts"}
          >
            <BellRing size={16} aria-hidden="true" />
            {alertCount > 0 ? (
              <span className="bp-shell__alerts-count" aria-hidden="true">
                {alertCount}
              </span>
            ) : null}
          </button>
        </div>

        <ErrorBanner />
        <main className="bp-main">
          <Suspense fallback={<RouteFallback />}>
            <Outlet />
          </Suspense>
        </main>
      </div>

      {alertsOpen ? (
        <AlertsPanel
          alerts={alerts.data?.alerts ?? []}
          loading={alerts.isLoading}
          onClose={() => setAlertsOpen(false)}
        />
      ) : null}
    </div>
  );
}

export default AppShell;
